import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Autocomplete,
    Button,
    Card,
    CardContent,
    Chip,
    Alert,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    AddCircle as AddCircleIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

function ThemePackEditor() {
    const { currentUser } = useAuth();
    const [themePacks, setThemePacks] = useState([]);
    const [availableChannels, setAvailableChannels] = useState([]);
    const [selectedPack, setSelectedPack] = useState(null);
    const [packChannels, setPackChannels] = useState([]);
    const [channelToAdd, setChannelToAdd] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [alert, setAlert] = useState(null);
    
    // State for Create Channel Dialog
    const [createChannelDialog, setCreateChannelDialog] = useState(false);
    const [newChannel, setNewChannel] = useState({
        displayName: '',
        channelKey: '',
        channelType: 'theme',
        searchAliases: [],
        initialPack: null
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [packsRes, channelsRes] = await Promise.all([
                axios.get('/api/tv-packages-v2/theme-packs'),
                axios.get('/api/tv-packages-v2/channels')
            ]);
            
            setThemePacks(packsRes.data.themePacks || []);
            // Show all channels except theme-pack bundles
            const individualChannels = (channelsRes.data.channels || []).filter(
                ch => ch.type !== 'theme-pack'
            );
            setAvailableChannels(individualChannels);
        } catch (error) {
            console.error('Error fetching data:', error);
            setAlert({ type: 'error', message: 'Failed to load theme packs and channels' });
        }
        setLoading(false);
    };

    const handleSelectPack = async (pack) => {
        setSelectedPack(pack);
        try {
            const response = await axios.get(`/api/tv-packages-v2/theme-pack-channels/${pack.pack_id}`);
            setPackChannels(response.data.channels || []);
        } catch (error) {
            console.error('Error fetching pack channels:', error);
            setAlert({ type: 'error', message: 'Failed to load pack channels' });
        }
    };

    const handleAddChannel = async () => {
        if (!channelToAdd || !selectedPack) return;

        setSaving(true);
        try {
            await axios.post('/api/tv-packages-v2/add-channel-to-pack', {
                packId: selectedPack.pack_id,
                channelId: channelToAdd.channel_id
            });

            // Refresh pack channels
            await handleSelectPack(selectedPack);
            setChannelToAdd(null);
            setAlert({ type: 'success', message: `Added ${channelToAdd.name} to ${selectedPack.display_name}` });
        } catch (error) {
            console.error('Error adding channel:', error);
            setAlert({ type: 'error', message: 'Failed to add channel to pack' });
        }
        setSaving(false);
    };

    const handleRemoveChannel = async (channelId, channelName) => {
        if (!selectedPack) return;

        setSaving(true);
        try {
            await axios.delete('/api/tv-packages-v2/remove-channel-from-pack', {
                data: { packId: selectedPack.pack_id, channelId }
            });

            // Refresh pack channels
            await handleSelectPack(selectedPack);
            setAlert({ type: 'success', message: `Removed ${channelName} from ${selectedPack.display_name}` });
        } catch (error) {
            console.error('Error removing channel:', error);
            setAlert({ type: 'error', message: 'Failed to remove channel from pack' });
        }
        setSaving(false);
    };

    const handleCreateChannel = async () => {
        if (!newChannel.displayName || !newChannel.channelType) {
            setAlert({ type: 'error', message: 'Display name and channel type are required' });
            return;
        }

        setSaving(true);
        try {
            // Create the channel
            const response = await axios.post('/api/tv-packages-v2/admin/channels', {
                channelKey: newChannel.channelKey || newChannel.displayName.toLowerCase().replace(/[^a-z0-9]/g, ''),
                displayName: newChannel.displayName,
                channelType: newChannel.channelType,
                searchAliases: newChannel.searchAliases.length > 0 ? newChannel.searchAliases : [newChannel.displayName]
            });

            // If initial pack is selected, add the channel to it
            if (newChannel.initialPack && response.data.success) {
                try {
                    await axios.post('/api/tv-packages-v2/add-channel-to-pack', {
                        packId: newChannel.initialPack,
                        channelId: response.data.channelId
                    });
                    setAlert({ type: 'success', message: `Created "${newChannel.displayName}" and added to theme pack` });
                } catch (packError) {
                    setAlert({ type: 'warning', message: `Channel created but failed to add to theme pack: ${packError.message}` });
                }
            } else {
                setAlert({ type: 'success', message: `Successfully created channel "${newChannel.displayName}"` });
            }

            // Reset form and close dialog
            setNewChannel({
                displayName: '',
                channelKey: '',
                channelType: 'theme',
                searchAliases: [],
                initialPack: null
            });
            setCreateChannelDialog(false);
            
            // Refresh data to show new channel
            await fetchData();
            
            // If a pack was selected and channel was added to it, refresh that pack
            if (newChannel.initialPack && selectedPack?.pack_id === newChannel.initialPack) {
                await handleSelectPack(selectedPack);
            }
        } catch (error) {
            console.error('Error creating channel:', error);
            if (error.response?.status === 409) {
                setAlert({ type: 'error', message: 'A channel with this key already exists' });
            } else {
                setAlert({ type: 'error', message: 'Failed to create channel' });
            }
        }
        setSaving(false);
    };

    const generateChannelKey = (displayName) => {
        return displayName.toLowerCase()
            .replace(/&/g, 'and')
            .replace(/[^a-z0-9]/g, '');
    };

    if (loading) {
        return <Typography>Loading theme pack editor...</Typography>;
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                    <Typography variant="h4" gutterBottom>
                        Theme Pack Editor
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Add or remove channels from existing theme packs
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<AddCircleIcon />}
                    onClick={() => setCreateChannelDialog(true)}
                    size="large"
                >
                    Create New Channel
                </Button>
            </Box>

            {alert && (
                <Alert 
                    severity={alert.type} 
                    sx={{ mb: 3 }} 
                    onClose={() => setAlert(null)}
                >
                    {alert.message}
                </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 3 }}>
                {/* Theme Packs List */}
                <Paper sx={{ p: 2, minWidth: 300, maxWidth: 400 }}>
                    <Typography variant="h6" gutterBottom>
                        Select Theme Pack to Edit
                    </Typography>
                    <List>
                        {themePacks.map((pack) => (
                            <ListItem
                                key={pack.pack_id}
                                button
                                selected={selectedPack?.pack_id === pack.pack_id}
                                onClick={() => handleSelectPack(pack)}
                            >
                                <ListItemText 
                                    primary={pack.display_name}
                                    secondary={`${pack.channel_count || 0} channels`}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Paper>

                {/* Pack Editor */}
                {selectedPack && (
                    <Paper sx={{ p: 2, flex: 1 }}>
                        <Typography variant="h6" gutterBottom>
                            Editing: {selectedPack.display_name}
                        </Typography>

                        {/* Add Channel Section */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Add Channel to Pack
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <Autocomplete
                                    value={channelToAdd}
                                    onChange={(event, newValue) => setChannelToAdd(newValue)}
                                    options={availableChannels.filter(ch => 
                                        !packChannels.some(pc => pc.channel_id === ch.channel_id)
                                    )}
                                    getOptionLabel={(option) => option.name}
                                    sx={{ minWidth: 300 }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Search for channel to add..."
                                        />
                                    )}
                                />
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddChannel}
                                    disabled={!channelToAdd || saving}
                                >
                                    Add
                                </Button>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* Current Channels */}
                        <Typography variant="subtitle1" gutterBottom>
                            Current Channels ({packChannels.length})
                        </Typography>
                        <List>
                            {packChannels.map((channel) => (
                                <ListItem key={channel.channel_id}>
                                    <ListItemText 
                                        primary={channel.display_name}
                                        secondary={channel.channel_key}
                                    />
                                    <ListItemSecondaryAction>
                                        <IconButton
                                            edge="end"
                                            color="error"
                                            onClick={() => handleRemoveChannel(
                                                channel.channel_id, 
                                                channel.display_name
                                            )}
                                            disabled={saving}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                )}
            </Box>

            {/* Create Channel Dialog */}
            <Dialog 
                open={createChannelDialog} 
                onClose={() => setCreateChannelDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Create New Channel</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <TextField
                        fullWidth
                        label="Display Name"
                        value={newChannel.displayName}
                        onChange={(e) => {
                            const displayName = e.target.value;
                            setNewChannel({
                                ...newChannel,
                                displayName,
                                channelKey: generateChannelKey(displayName)
                            });
                        }}
                        margin="normal"
                        required
                        helperText="The name that will be displayed (e.g., 'A&E')"
                    />
                    
                    <TextField
                        fullWidth
                        label="Channel Key"
                        value={newChannel.channelKey}
                        onChange={(e) => setNewChannel({ ...newChannel, channelKey: e.target.value })}
                        margin="normal"
                        helperText="Auto-generated identifier (can be edited if needed)"
                    />
                    
                    <FormControl fullWidth margin="normal" required>
                        <InputLabel>Channel Type</InputLabel>
                        <Select
                            value={newChannel.channelType}
                            onChange={(e) => setNewChannel({ ...newChannel, channelType: e.target.value })}
                            label="Channel Type"
                        >
                            <MenuItem value="theme">Theme Channel</MenuItem>
                            <MenuItem value="core">Core Channel</MenuItem>
                            <MenuItem value="premium">Premium Channel</MenuItem>
                        </Select>
                        <FormHelperText>Select the type of channel</FormHelperText>
                    </FormControl>
                    
                    <Autocomplete
                        multiple
                        freeSolo
                        options={[]}
                        value={newChannel.searchAliases}
                        onChange={(event, newValue) => {
                            setNewChannel({ ...newChannel, searchAliases: newValue });
                        }}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                label="Search Aliases"
                                placeholder="Press Enter to add aliases"
                                margin="normal"
                                helperText="Alternative names for search (e.g., 'A and E', 'Arts and Entertainment')"
                            />
                        )}
                    />
                    
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Add to Theme Pack (Optional)</InputLabel>
                        <Select
                            value={newChannel.initialPack || ''}
                            onChange={(e) => setNewChannel({ ...newChannel, initialPack: e.target.value || null })}
                            label="Add to Theme Pack (Optional)"
                        >
                            <MenuItem value="">
                                <em>Don't add to any pack</em>
                            </MenuItem>
                            {themePacks.map((pack) => (
                                <MenuItem key={pack.pack_id} value={pack.pack_id}>
                                    {pack.display_name}
                                </MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>Optionally add this channel to a theme pack immediately</FormHelperText>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateChannelDialog(false)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleCreateChannel}
                        variant="contained"
                        disabled={saving || !newChannel.displayName || !newChannel.channelType}
                    >
                        {saving ? 'Creating...' : 'Create Channel'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default ThemePackEditor;